import React, { useState, useEffect } from 'react';
import { Container, Grid, Box, Typography, Pagination, Card, CardContent, Button, Alert } from '@mui/material';
import { Home as HomeIcon, Person as PersonIcon } from '@mui/icons-material';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import RoomSearchFilters from '../components/search/RoomSearchFilters';
import RoomCard from '../components/room/RoomCard';
import Seo from '../components/common/Seo';
import Breadcrumbs from '../components/common/Breadcrumbs';
import { getSearchResultSchema, getBreadcrumbSchema } from '../utils/structuredDataHelpers';
import { getRegionalTitle, getRegionalDescription } from '../utils/seoHelpers';

function RoomSearch() {
    const location = useLocation();
    const navigate = useNavigate();
    const { currentUser } = useAuth();
    const queryParams = new URLSearchParams(location.search);

    // URLからフィルター状態を初期化
    const initialFilters = {
        location: queryParams.get('location') || '',
        priceRange: [
            parseInt(queryParams.get('minPrice')) || 0,
            parseInt(queryParams.get('maxPrice')) || 200000
        ],
        propertyType: queryParams.get('propertyType') || '',
        roomType: queryParams.get('roomType') || '',
        availabilityType: queryParams.get('availabilityType') || '',
        moveInTiming: queryParams.get('moveInTiming') || '',
        selectedAmenities: queryParams.get('amenities') ? queryParams.get('amenities').split(',') : [],
        sortBy: queryParams.get('sortBy') || 'date_desc'
    };

    const [filters, setFilters] = useState(initialFilters);
    const [searchResults, setSearchResults] = useState({ rooms: [], totalPages: 0, currentPage: 1 });
    const [isLoading, setIsLoading] = useState(false);
    const [page, setPage] = useState(parseInt(queryParams.get('page')) || 1);
    const [totalPages, setTotalPages] = useState(1);
    const [hasSampleRooms, setHasSampleRooms] = useState(false);

    // フィルター変更時にURLを更新
    useEffect(() => {
        const params = new URLSearchParams();
        if (filters.location) params.set('location', filters.location);
        if (filters.priceRange[0] > 0) params.set('minPrice', filters.priceRange[0]);
        if (filters.priceRange[1] < 200000) params.set('maxPrice', filters.priceRange[1]);
        if (filters.propertyType) params.set('propertyType', filters.propertyType);
        if (filters.roomType) params.set('roomType', filters.roomType);
        if (filters.availabilityType) params.set('availabilityType', filters.availabilityType);
        if (filters.moveInTiming) params.set('moveInTiming', filters.moveInTiming);
        if (filters.selectedAmenities.length > 0) params.set('amenities', filters.selectedAmenities.join(','));
        if (filters.sortBy !== 'date_desc') params.set('sortBy', filters.sortBy);
        if (page > 1) params.set('page', page);

        navigate({ search: params.toString() }, { replace: true });
    }, [filters, page, navigate]);

    useEffect(() => {
        handleSearch();
    }, [page, filters]);

    const handleSearch = async () => {
        setIsLoading(true);
        try {
            const params = {
                minPrice: filters.priceRange[0],
                maxPrice: filters.priceRange[1],
                propertyType: filters.propertyType,
                roomType: filters.roomType,
                availabilityType: filters.availabilityType,
                moveInTiming: filters.moveInTiming,
                amenities: filters.selectedAmenities,
                location: filters.location,
                sortBy: filters.sortBy,
                page
            };

            const response = await axios.get('/api/rooms', { params });
            setSearchResults(response.data);
            setTotalPages(response.data.totalPages);
            setHasSampleRooms(response.data.sampleRoomsCount > 0);
        } catch (error) {
            console.error('検索エラー:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handlePageChange = (event, value) => {
        setPage(value);
        window.scrollTo({
            top: 0,
            behavior: 'smooth'
        });
    };

    const handleClear = () => {
        setFilters({
            location: '',
            priceRange: [0, 200000],
            propertyType: '',
            roomType: '',
            availabilityType: '',
            moveInTiming: '',
            selectedAmenities: [],
            sortBy: 'date_desc'
        });
        setPage(1);
    };

    // SEO用の関数を追加
    // 地域名に特化したタイトル
    const getPageTitle = () => {
        return getRegionalTitle(filters.location, 'room');
    };

    // 地域特化の説明文
    const getPageDescription = () => {
        return getRegionalDescription(filters.location, 'room');
    };

    // カノニカルURL生成
    const getCanonicalPath = () => {
        if (!filters.location) {
            return '/room-search';
        }

        return `/room-search?location=${encodeURIComponent(filters.location)}`;
    };

    // 地域特化コンテンツを追加
    const renderLocationSpecificContent = () => {
        if (filters.location) {
            return (
                <Box mt={3} mb={4}>
                    <Typography variant="h5" gutterBottom>{filters.location}のルームシェア物件</Typography>
                    <Typography variant="body1">
                        {filters.location}エリアでルームシェアをお探しの方向けの物件情報です。
                        理想の住まいを見つけるために、エリア、家賃、設備などの条件で絞り込みができます。
                    </Typography>
                </Box>
            );
        }

        return null;
    };

    // パンくずリスト用データを追加
    const getBreadcrumbs = () => {
        const breadcrumbs = [
            { name: 'トップ', path: '/' },
            { name: 'ルームシェア物件検索', path: '/room-search' }
        ];

        // 地域が指定されている場合は追加
        if (filters.location) {
            breadcrumbs.push({
                name: filters.location,
                path: `/room-search?location=${encodeURIComponent(filters.location)}`
            });
        }

        return breadcrumbs;
    };

    // 構造化データの配列を生成
    const getSchemaArray = () => {
        // 検索結果のスキーマ
        const searchSchema = getSearchResultSchema(searchResults.rooms);
        // パンくずリストのスキーマ
        const breadcrumbSchema = getBreadcrumbSchema(getBreadcrumbs());

        return [searchSchema, breadcrumbSchema].filter(Boolean);
    };

    return (
        <>
            {/* SEOコンポーネントを追加 */}
            <Seo
                title={getPageTitle()}
                description={getPageDescription()}
                path={getCanonicalPath()}
                location={filters.location}
                schema={getSchemaArray()}
                metaRobots={hasSampleRooms ? 'noindex, nofollow' : undefined}
            />

            <Box
                sx={{
                    position: 'sticky',
                    top: { xs: '56px', sm: '64px' },
                    zIndex: 1100,
                    backgroundColor: 'transparent',
                    borderBottom: '1px solid rgba(0, 0, 0, 0.05)',
                    transition: 'all 0.3s ease-in-out',
                    py: { xs: 0.5, sm: 1 },
                    mb: { xs: 2, sm: 1 },
                }}
            >
                <Container maxWidth="lg">
                    <RoomSearchFilters
                        filters={filters}
                        setFilters={setFilters}
                        totalRooms={searchResults.totalRooms}
                        onSearch={handleSearch}
                        onClear={handleClear}
                    />
                </Container>
            </Box>

            <Container maxWidth="lg">
                {/* パンくずリストを追加 */}
                <Breadcrumbs items={getBreadcrumbs()} />

                {/* 地域特化コンテンツを追加 */}
                {renderLocationSpecificContent()}

                {/* 新規ユーザー向けの案内 */}
                {!currentUser && (
                    <Alert
                        severity="info"
                        sx={{
                            mb: 3,
                            background: 'linear-gradient(135deg, rgba(52, 152, 219, 0.1), rgba(93, 173, 226, 0.05))',
                            border: '1px solid rgba(52, 152, 219, 0.2)',
                            borderRadius: '12px',
                            '& .MuiAlert-message': {
                                width: '100%'
                            }
                        }}
                    >
                        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'stretch', sm: 'center' }, gap: 2 }}>
                            <Box sx={{ flex: 1 }}>
                                <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                                    <HomeIcon sx={{ verticalAlign: 'middle', mr: 0.5, fontSize: '1.1rem' }} />
                                    すぐに住める物件を探しています
                                </Typography>
                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                    既に物件があるオーナーが、一緒に住むルームメイトを募集中の投稿です
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={() => navigate('/renters')}
                                    startIcon={<PersonIcon />}
                                    sx={{
                                        fontSize: '0.75rem',
                                        borderColor: '#3498db',
                                        color: '#3498db',
                                        '&:hover': {
                                            backgroundColor: 'rgba(52, 152, 219, 0.08)',
                                        }
                                    }}
                                >
                                    相手を探す
                                </Button>
                                <Button
                                    variant="contained"
                                    size="small"
                                    onClick={() => navigate('/create-listing')}
                                    sx={{
                                        fontSize: '0.75rem',
                                        background: 'linear-gradient(135deg, #3498db, #5dade2)',
                                        '&:hover': {
                                            background: 'linear-gradient(135deg, #2980b9, #3498db)',
                                        }
                                    }}
                                >
                                    物件登録
                                </Button>
                            </Box>
                        </Box>
                    </Alert>
                )}

                <Box sx={{ mt: { xs: 1, sm: 0 } }}>
                    {isLoading ? (
                        <Box textAlign="center">
                            <Typography>読み込み中...</Typography>
                        </Box>
                    ) : searchResults.rooms.length > 0 ? (
                        <>
                            <Box display="flex" justifyContent="center">
                                <Typography color="text.secondary" sx={{ mb: 0.5, fontSize: '0.875rem' }}>
                                    全{searchResults.totalRooms}件中
                                    {(searchResults.currentPage - 1) * 12 + 1}
                                    ～
                                    {Math.min(searchResults.currentPage * 12, searchResults.totalRooms)}件を表示
                                </Typography>
                            </Box>
                            <Grid container spacing={2}>
                                {searchResults.rooms.map((room) => (
                                    <Grid item xs={12} sm={6} md={4} key={room._id}>
                                        <RoomCard room={room} />
                                    </Grid>
                                ))}
                            </Grid>
                            {searchResults.rooms.length > 0 && (
                                <Box display="flex" justifyContent="center" mt={4}>
                                    <Pagination
                                        count={totalPages}
                                        page={page}
                                        onChange={handlePageChange}
                                        color="primary"
                                    />
                                </Box>
                            )}
                        </>
                    ) : (
                        <Box textAlign="center" my={4}>
                            <Typography color="text.secondary">
                                {filters.location ? '検索条件に一致する物件が見つかりませんでした' : '物件を読み込んでいます...'}
                            </Typography>
                        </Box>
                    )}
                </Box>
            </Container>
        </>
    );
}

export default RoomSearch; 